import { useEffect, useState } from 'react';
import { v2, ApiError, type MeResponse } from '@/api/v2Client';

export interface UseUserPermissionsResult {
  readonly loading: boolean;
  readonly email: string | null;
  readonly roles: ReadonlyArray<{ Id: string; Name: string; Description: string }>;
  readonly permissions: ReadonlyArray<{ Resource: string; Action: string }>;
  readonly hasPermission: (resource: string, action: string) => boolean;
  readonly hasRole: (roleName: string) => boolean;
  /**
   * TRUE when the /users/me lookup itself FAILED (outage, 5xx, network), so
   * the permissions above are UNKNOWN — not "none". A surface that would
   * otherwise render "access denied" / "ask an administrator" must branch on
   * this first and show an unknown/retry state instead, or a transient backend
   * blip silently tells an authorized user they lack access. Distinct from a
   * cleanly loaded user who genuinely holds no matching permission.
   */
  readonly failed: boolean;
  /**
   * T-06 R05 — whether the server will accept invitation create / resend /
   * accept right now. FALSE whenever the backend did not say otherwise,
   * which is the same direction the server defaults in: a missing answer
   * must not light up a button the server will refuse.
   */
  readonly invitesEnabled: boolean;
  readonly refresh: () => Promise<void>;
}

interface PermissionsCache {
  state:
    | { kind: 'idle' }
    /**
     * phase-roles-and-users-fixes — `previous` carries the already-loaded
     * permissions through a REFRESH. Without it, `refresh()` reported
     * `loading: true` to every consumer, and each admin page that early-returns
     * on `perms.loading` blanked its entire tree mid-interaction — unmounting
     * any open dialog and destroying its state. That is what made the
     * Edit-roles toggle look one-way (see the report): the dialog remounted
     * after every change and re-read a stale snapshot. Stale-while-revalidate
     * keeps the last known permissions on screen while the refresh runs.
     */
    | { kind: 'loading'; promise: Promise<void>; previous?: MeResponse }
    | { kind: 'loaded'; data: MeResponse }
    | { kind: 'error'; at: number };
  listeners: Set<() => void>;
}

const cache: PermissionsCache = {
  state: { kind: 'idle' },
  listeners: new Set(),
};

/** How long a failed /users/me sticks before the next mount retries it. */
export const PERMISSIONS_ERROR_RETRY_MS = 5_000;

function notify(): void {
  for (const fn of cache.listeners) fn();
}

async function fetchMe(): Promise<void> {
  try {
    const data = await v2.users.getMe();
    cache.state = { kind: 'loaded', data };
  } catch (err) {
    // ApiError 401 or any other failure → safe defaults so the UI keeps
    // working even when the backend can't identify the user.
    if (!(err instanceof ApiError)) {
      console.warn('[useUserPermissions] unexpected fetch error', err);
    }
    cache.state = { kind: 'error', at: Date.now() };
  } finally {
    notify();
  }
}

function ensureLoad(): void {
  // phase-dashboard-coherence item 1 — the error state used to be STICKY for
  // the whole SPA session: one transient /users/me failure (the wounded-
  // backend window right after a post-land sync restart is the documented
  // case) blanked the admin menu and every role-gated affordance until a
  // hard reload, while the un-gated menu items kept working — exactly the
  // "admin entries vanished from the menu" sighting. An error now RETRIES on
  // the next hook mount once a short cool-off has passed, so a recovered
  // backend heals the UI without a reload.
  const retryableError =
    cache.state.kind === 'error' && Date.now() - cache.state.at >= PERMISSIONS_ERROR_RETRY_MS;
  if (cache.state.kind === 'idle' || retryableError) {
    cache.state = { kind: 'loading', promise: fetchMe() };
  }
}

/**
 * Reset the module-level cache. Exported for tests so each test starts
 * with a clean slate; production code never needs this (use `refresh()`
 * to force a refetch instead).
 */
export function __resetUserPermissionsCacheForTests(): void {
  cache.state = { kind: 'idle' };
  cache.listeners.clear();
}

/** Test-only: age the error state so the retry cool-off can be crossed
 *  without fake timers (the cool-off compares wall-clock stamps). */
export function __setUserPermissionsErrorAtForTests(at: number): void {
  if (cache.state.kind === 'error') cache.state = { kind: 'error', at };
}

export function useUserPermissions(): UseUserPermissionsResult {
  const [, force] = useState(0);

  useEffect(() => {
    ensureLoad();
    const tick = (): void => force((n) => n + 1);
    cache.listeners.add(tick);
    return () => {
      cache.listeners.delete(tick);
    };
  }, []);

  const previous = cache.state.kind === 'loading' ? (cache.state.previous ?? null) : null;
  const data = cache.state.kind === 'loaded' ? cache.state.data : previous;
  // Only a load with NOTHING to show is "loading". A refresh over existing
  // data revalidates in the background and never blanks the page.
  const loading =
    cache.state.kind === 'idle' || (cache.state.kind === 'loading' && previous === null);

  const permissions = data?.Permissions ?? [];
  const roles = data?.Roles ?? [];

  return {
    loading,
    email: data?.Email ?? null,
    roles,
    permissions,
    hasPermission: (resource, action) =>
      permissions.some((p) => p.Resource === resource && p.Action === action),
    hasRole: (roleName) => roles.some((r) => r.Name === roleName),
    failed: cache.state.kind === 'error',
    // Absent (older backend, or /users/me failed) reads as OFF — the same
    // direction the server defaults in.
    invitesEnabled: data?.Features?.invitesEnabled === true,
    refresh: async () => {
      const carried = cache.state.kind === 'loaded' ? cache.state.data : undefined;
      cache.state = {
        kind: 'loading',
        promise: fetchMe(),
        ...(carried !== undefined && { previous: carried }),
      };
      notify();
      await cache.state.promise;
    },
  };
}
